import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../API';
import { useAuth } from '../../providers/AuthContext';
import LoginWidget from '../../widgets/LoginWidget';
import ProjectItemListWithProgress from './ProjectItemListWithProgress';
import ProjectsHubBackLink from './hub/ProjectsHubBackLink';
import styles from './ProjectHomepage.module.css';

const ACCESS_WRITE = 1;

/**
 * Slide Set landing — analogous to ProjectHomepage (participate).
 * Title, open first slide, item list; settings for users with write access.
 */
function SlideSetLanding() {
    const { folderId } = useParams();
    const navigate = useNavigate();
    const { user, pending } = useAuth();

    const [doc, setDoc] = useState(null);
    const [items, setItems] = useState([]);
    const [firstItemId, setFirstItemId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [needsLogin, setNeedsLogin] = useState(false);

    useEffect(() => {
        if (!folderId) return;
        setLoading(true);
        setError(null);
        setNeedsLogin(false);

        Promise.all([
            api.get(`projects/${folderId}`, {}),
            api.get(`projects/${folderId}/items`, {}),
        ])
            .then(([folderRes, itemsRes]) => {
                if (folderRes.status === 401 || itemsRes.status === 401) {
                    setNeedsLogin(true);
                    setDoc(null);
                    setItems([]);
                    return;
                }
                if (folderRes.status === 403 || itemsRes.status === 403) {
                    setError('You do not have access to this Slide Set.');
                    setDoc(null);
                    setItems([]);
                    return;
                }
                if (folderRes.status !== 200) {
                    setError(folderRes.data?.message || 'Failed to load Slide Set.');
                    return;
                }
                if (folderRes.data?.girderProjects?.type !== 'slideset') {
                    setError('This folder is not a Slide Set.');
                    return;
                }
                setDoc(folderRes.data);
                const list = itemsRes.status === 200 ? (itemsRes.data?.items || []) : [];
                setItems(list);
                setFirstItemId(list[0]?._id ? String(list[0]._id) : null);
                if (itemsRes.status !== 200 && itemsRes.status !== 401) {
                    // Soft-fail items: still show landing with empty list if folder loaded
                    if (list.length === 0 && itemsRes.data?.message) {
                        setError(itemsRes.data.message);
                    }
                }
            })
            .catch((e) => {
                if (e?.response?.status === 401) setNeedsLogin(true);
                else setError(e.message || 'Failed to load.');
            })
            .finally(() => setLoading(false));
    }, [folderId]);

    if (pending || loading) {
        return <div className={styles.loading}>Loading Slide Set…</div>;
    }

    if (needsLogin && !user) {
        return <LoginWidget alwaysOpen title="Log in to view this private Slide Set." />;
    }

    if (error) {
        return (
            <div className={styles.page}>
                <div className={styles.errorMessage}>{error}</div>
                <ProjectsHubBackLink className={styles.backLink} variant="long" />
            </div>
        );
    }

    if (!doc) {
        return (
            <div className={styles.page}>
                <p>Slide Set not found.</p>
                <ProjectsHubBackLink className={styles.backLink} variant="long" />
            </div>
        );
    }

    const canManage = typeof doc.myAccess === 'number' && doc.myAccess >= ACCESS_WRITE;
    const canOpen = Boolean(firstItemId);
    const isPublic = Boolean(doc.public);
    const instructions = String(doc.girderProjects?.slideSet?.instructions ?? '').trim();

    return (
        <div className={styles.page}>
            <div className={styles.pageHeader}>
                <div className={styles.headerLeft}>
                    <ProjectsHubBackLink className={styles.backLink} variant="short" />
                    <h3 className={styles.pageTitle}>{doc.name}</h3>
                    {isPublic ? (
                        <span className={styles.roleBadge} style={{ background: '#0969da' }}>
                            Public
                        </span>
                    ) : null}
                </div>
                {canManage ? (
                    <div className={styles.headerActions}>
                        <button
                            type="button"
                            className={styles.buttonSecondary}
                            onClick={() => navigate(`/projects/${folderId}`)}
                        >
                            ✎ Manage Slide Set
                        </button>
                    </div>
                ) : null}
            </div>

            {instructions ? (
                <div className={styles.instructionsCard}>
                    <h4 className={styles.sectionTitle}>Description</h4>
                    <p className={styles.instructionsText}>{instructions}</p>
                </div>
            ) : (
                <div className={styles.instructionsCard}>
                    <p className={styles.noInstructions}>
                        {canManage
                            ? 'No description has been provided for this Slide Set.'
                            : 'The Slide Set owner has not added a description yet.'}
                    </p>
                </div>
            )}

            <div className={styles.actionCard}>
                <div className={styles.annotationTaskHeader}>
                    <h4 className={styles.sectionTitle}>Slides</h4>
                    <button
                        type="button"
                        className={styles.buttonOpen}
                        onClick={() => navigate(`/slidesets/${folderId}/view/${firstItemId}`)}
                        disabled={!canOpen}
                        title={!firstItemId ? 'No slides available yet' : 'Open slide viewer'}
                    >
                        Open ▶
                    </button>
                </div>
                <p className={styles.actionDescription}>
                    {firstItemId
                        ? 'Open the viewer to browse slides. Click a slide below to jump to it.'
                        : canManage
                            ? 'No slides yet. Use Manage Slide Set to add items.'
                            : 'No slides have been added to this Slide Set yet.'}
                </p>
                {items.length > 0 ? (
                    <div className={styles.progressListWrap}>
                        <ProjectItemListWithProgress
                            items={items}
                            progressMap={{}}
                            showProgressBadge={false}
                            onSelectItem={(item) =>
                                navigate(`/slidesets/${folderId}/view/${item._id}`)
                            }
                        />
                    </div>
                ) : null}
            </div>
        </div>
    );
}

export default SlideSetLanding;
